import api from './api';

export interface FinancialRiskSummary {
  total_assessments: number;
  total_expected_value: number;
  residual_assessments: number;
  residual_expected_value: number;
  mitigated_expected_value: number;
  by_impact: Record<string, { count: number; expected_value: number }>;
  by_category: Record<string, { count: number; expected_value: number }>;
  by_impact_residual: Record<string, { count: number; expected_value: number }>;
  by_category_residual: Record<string, { count: number; expected_value: number }>;
}

export interface FinancialRiskAssessment {
  id: number;
  event_id: number;
  company_id: number;
  risk_category: string;
  impact_level: string;
  estimated_cost_min: number;
  estimated_cost_max: number;
  expected_value: number;
  probability: number;
  timeframe: string;
  created_at: string;
  updated_at: string;
}

export interface FinancialRiskTrend {
  date: string;
  count: number;
  total_expected_value: number;
}

export interface FinancialRiskTrends {
  period: {
    start_date: string;
    end_date: string;
  };
  trends: FinancialRiskTrend[];
}

export interface TopRisk {
  id: number;
  event_id: number;
  risk_category: string;
  impact_level: string;
  timeframe: string;
  expected_value: number;
  probability: number;
  created_at: string;
}

export interface TopRisksResponse {
  top_risks: TopRisk[];
}

export interface PaginatedAssessments {
  assessments: FinancialRiskAssessment[];
  total: number;
  limit: number;
  offset: number;
}

class FinancialRiskService {
  /**
   * Obtém resumo de riscos financeiros da empresa
   */
  async getCompanyRiskSummary(companyId: number): Promise<FinancialRiskSummary> {
    const response = await api.get(`/api/v2/financial-risk/company/${companyId}/summary`);
    return response.data;
  }

  /**
   * Obtém avaliações de risco financeiro paginadas
   */
  async getCompanyRiskAssessments(
    companyId: number,
    limit: number = 10,
    offset: number = 0,
  ): Promise<PaginatedAssessments> {
    const response = await api.get(`/api/v2/financial-risk/company/${companyId}/assessments`, {
      params: { limit, offset },
    });
    return response.data;
  }

  /**
   * Obtém tendências de risco financeiro
   */
  async getCompanyRiskTrends(companyId: number, days: number = 30): Promise<FinancialRiskTrends> {
    const response = await api.get(`/api/v2/financial-risk/company/${companyId}/trends`, {
      params: { days },
    });
    return response.data;
  }

  /**
   * Obtém principais riscos da empresa
   */
  async getCompanyTopRisks(companyId: number, limit: number = 5): Promise<TopRisksResponse> {
    const response = await api.get(`/api/v2/financial-risk/company/${companyId}/top-risks`, {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Avalia risco financeiro de um evento específico
   */
  async assessEventRisk(eventId: number): Promise<FinancialRiskAssessment> {
    const response = await api.post(`/api/v2/financial-risk/event/${eventId}/assess`);
    return response.data;
  }

  /**
   * Formata valor monetário para exibição
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  /**
   * Formata porcentagem para exibição
   */
  formatPercentage(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  }

  /**
   * Obtém cor baseada no nível de impacto
   */
  getImpactColor(impactLevel: string): string {
    switch (impactLevel.toLowerCase()) {
      case 'critical':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Obtém cor de fundo baseada no nível de impacto
   */
  getImpactBgColor(impactLevel: string): string {
    switch (impactLevel.toLowerCase()) {
      case 'critical':
        return 'bg-red-50';
      case 'high':
        return 'bg-orange-50';
      case 'medium':
        return 'bg-yellow-50';
      case 'low':
        return 'bg-blue-50';
      default:
        return 'bg-gray-50';
    }
  }

  /**
   * Obtém ícone baseado na categoria de risco
   */
  getRiskCategoryIcon(category: string): string {
    switch (category.toLowerCase()) {
      case 'operational':
        return '⚙️';
      case 'financial':
        return '💰';
      case 'reputational':
        return '🏢';
      case 'compliance':
        return '📋';
      case 'strategic':
        return '🎯';
      default:
        return '⚠️';
    }
  }
}

export default new FinancialRiskService();
